import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { CachedTags, Tag, TagType, TagsResponse } from '../entities/tags.entity';

@Injectable({ providedIn: 'root' })
export class TagsInfrastructure {
  readonly #http = inject(HttpClient);
  readonly #authStateFacade = inject(AuthStateFacade);
  readonly #api = `${environment.apihost}/api`;
  readonly #cacheKeyPrefix = 'tags-settings';

  public getTags = (type: TagType): Observable<TagsResponse> => this.#http.get<TagsResponse>(`${this.#api}/tags?type=${type}`);

  public addTag = (tagId: string, entityId: string, name: string, type: TagType): Observable<Tag> =>
    this.#http.post<Tag>(`${this.#api}/tag/link`, { tagId, entityId, name, type });

  public deleteTag = (tagId: string): Observable<void> => this.#http.delete<void>(`${this.#api}/tag/${tagId}`);

  public deleteLinkedTag = (tagId: string, entityId: string): Observable<void> =>
    this.#http.delete<void>(`${this.#api}/tag/${tagId}/link/${entityId}`);

  public getPresetItems = (type: TagType, tags: Tag[]): Observable<CachedTags> => {
    const cachedTags = this.#getCachedItems(type);
    if (!cachedTags) {
      return of({ tagIds: [], displayTags: false, displayAllItemsWithoutTags: false });
    }

    const { tagIds, displayTags, displayAllItemsWithoutTags } = cachedTags;
    return of({
      tagIds: tagIds.filter((tagId: string) => tags.some((tag) => tag.tagId === tagId)),
      displayTags,
      displayAllItemsWithoutTags,
    });
  };

  public removeActiveTag(type: TagType, tagId: string): Observable<string[]> {
    const tagIds = this.getActivatedTagIds(type);
    return of(tagIds.splice(tagIds.indexOf(tagId), 1));
  }

  public resetTags = (): Observable<string[]> => of([]);

  public activateAllTags = (tags: Tag[]): Observable<string[]> => of(tags.map(({ tagId }) => tagId));

  public toggleTag(type: TagType, tagId: string): Observable<string[]> {
    const tagIds = this.getActivatedTagIds(type);
    tagIds.includes(tagId) ? tagIds.splice(tagIds.indexOf(tagId), 1) : tagIds.push(tagId);
    return of(tagIds);
  }

  public toggleTags = () => of(true);
  public activateAllWithoutTags = () => of(true);

  public getActivatedTagIds = (type: TagType): string[] => {
    const cachedTags = this.#getCachedItems(type);
    return cachedTags?.tagIds ? cachedTags.tagIds : [];
  };

  public cache = (type: TagType, tagIds: string[], displayAllItemsWithoutTags: boolean, displayTags: boolean): void =>
    localStorage.setItem(this.#getCacheKey(type), JSON.stringify({ tagIds, displayAllItemsWithoutTags, displayTags }));

  readonly #getCacheKey = (type: TagType): string => `${this.#cacheKeyPrefix}-${type}-${this.#authStateFacade.customerId}`;

  readonly #getCachedItems = (type: TagType): CachedTags => {
    const cacheKey = this.#getCacheKey(type);
    const cached = localStorage.getItem(cacheKey);
    try {
      return cached ? (JSON.parse(cached) as CachedTags) : null;
    } catch (error) {
      localStorage.removeItem(cacheKey);
      return null;
    }
  };
}
